import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, LoaderCircle } from "lucide-react";
import { US_STATES, DEBT_SITUATIONS, CONSENT_TEXT } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { first_name: "", last_name: "", email: "", phone: "", state: "", debt_situation: "" };

export const RegistrationForm = ({ variant = "hero", attribution }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please check the consent box to book your call.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ghl-lead`, {
        ...form,
        debt_situation: form.debt_situation || null,
        consent: true,
        consent_text: CONSENT_TEXT,
        attribution,
      });
      sessionStorage.setItem(
        "dfl_lead",
        JSON.stringify({
          leadId: res.data.leadId,
          firstName: form.first_name,
          lastName: form.last_name,
          email: form.email,
          phone: form.phone,
        })
      );
      navigate("/booking");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong — please try again.");
      setLoading(false);
    }
  };

  const field = "w-full bg-[#091E42]/40 border border-white/15 text-[#D9E4F5] text-sm px-4 py-3 placeholder:text-white/30 focus:border-[#FFFFFF] focus:outline-none transition-colors duration-200";

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${variant}-registration-form`}>
      <div className="grid grid-cols-2 gap-3">
        <input required data-testid={`${variant}-first-name-input`} className={field} placeholder="First Name" value={form.first_name} onChange={set("first_name")} />
        <input required data-testid={`${variant}-last-name-input`} className={field} placeholder="Last Name" value={form.last_name} onChange={set("last_name")} />
      </div>
      <input required type="email" data-testid={`${variant}-email-input`} className={field} placeholder="Email Address" value={form.email} onChange={set("email")} />
      <input required type="tel" data-testid={`${variant}-phone-input`} className={field} placeholder="Mobile Phone" value={form.phone} onChange={set("phone")} />
      <div className="grid grid-cols-2 gap-3">
        <select required data-testid={`${variant}-state-select`} className={field} value={form.state} onChange={set("state")}>
          <option value="" disabled>State</option>
          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid={`${variant}-debt-select`} className={field} value={form.debt_situation} onChange={set("debt_situation")}>
          <option value="">Debt Situation (Optional)</option>
          {DEBT_SITUATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      <label className="flex items-start gap-3 cursor-pointer pt-1">
        <input
          type="checkbox"
          data-testid={`${variant}-consent-checkbox`}
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 w-4 h-4 shrink-0 accent-[#FFFFFF]"
        />
        <span className="text-[10px] leading-relaxed text-white/35">{CONSENT_TEXT}</span>
      </label>
      <button
        type="submit"
        disabled={loading}
        data-testid={`${variant}-submit-btn`}
        data-cta="true"
        className="w-full bg-[#FFFFFF] text-[#0E2A54] font-bold text-sm tracking-wide py-4 hover:bg-[#DCE8FF] active:scale-[0.99] transition-[background-color,transform] duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {loading ? <LoaderCircle className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        BOOK MY FREE CALL
      </button>
      <p className="text-center text-[10px] text-white/25 pt-1">
        Free educational call · No obligation · $0 out of pocket
      </p>
    </form>
  );
};
